import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DollarSign,
  Play,
  Square,
  RotateCcw,
  Image,
  Video,
  TrendingUp,
  Calculator,
} from 'lucide-react';
import { calibration, CalibrationData } from '../api/client';
import { cn } from '../lib/utils';

export default function CostAnalysis() {
  const [imageCal, setImageCal] = useState<CalibrationData | null>(null);
  const [videoCal, setVideoCal] = useState<CalibrationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Projection inputs
  const [imagesPerMonth, setImagesPerMonth] = useState(50);
  const [videosPerMonth, setVideosPerMonth] = useState(5);
  const [subscriptionPrice, setSubscriptionPrice] = useState(9.99);
  const [subscribers, setSubscribers] = useState(100);

  const fetchCalibration = useCallback(async () => {
    try {
      const res = await calibration.get();
      setImageCal(res.calibration.image);
      setVideoCal(res.calibration.video);
      setError(null);
    } catch (err) {
      console.error('Failed to load calibration data:', err);
      setError('Failed to load calibration data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalibration();
  }, [fetchCalibration]);

  // Calibrated cost-per-unit rates
  const imageCostPerUnit = imageCal?.status === 'completed' && imageCal.costPerUnit ? imageCal.costPerUnit : 0;
  const videoCostPerUnit = videoCal?.status === 'completed' && videoCal.costPerUnit ? videoCal.costPerUnit : 0;

  // Computed projections — memoized so they only recompute when inputs change
  const projection = useMemo(() => {
    const imageCostPerUser = imagesPerMonth * imageCostPerUnit;
    const videoCostPerUser = videosPerMonth * videoCostPerUnit;
    const totalCostPerUser = imageCostPerUser + videoCostPerUser;

    const marginPerUser = subscriptionPrice - totalCostPerUser;
    const rawMarginPercent = subscriptionPrice > 0 ? (marginPerUser / subscriptionPrice) * 100 : 0;
    // Guard against NaN from edge cases (e.g. 0/0)
    const marginPercent = isNaN(rawMarginPercent) ? 0 : rawMarginPercent;

    const monthlyRevenue = subscriptionPrice * subscribers;
    const monthlyCost = totalCostPerUser * subscribers;
    const monthlyProfit = monthlyRevenue - monthlyCost;

    return {
      imageCostPerUser,
      videoCostPerUser,
      totalCostPerUser,
      marginPerUser,
      marginPercent,
      monthlyRevenue,
      monthlyCost,
      monthlyProfit,
    };
  }, [imagesPerMonth, videosPerMonth, imageCostPerUnit, videoCostPerUnit, subscriptionPrice, subscribers]);

  const hasAnyCalibration = (imageCal?.status === 'completed') || (videoCal?.status === 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <h1 className="text-3xl font-bold text-gradient">Cost Analysis</h1>
        <p className="text-muted-foreground mt-1">
          Calibrate RunPod costs and model subscription pricing
        </p>
      </div>

      {error && (
        <div className="rounded-xl bg-rose-500/10 border border-rose-500/20 p-4 text-rose-400 text-sm">
          {error}
        </div>
      )}

      {/* Card 1: Cost Calibration */}
      <div className="card-glow rounded-2xl p-6 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
            <Calculator className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Cost Calibration</h2>
            <p className="text-xs text-muted-foreground">
              Run separate calibration sessions for images and videos
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <CalibrationPanel
            type="image"
            icon={<Image className="w-5 h-5" />}
            label="Image Generation"
            accentColor="violet"
            data={imageCal}
            onUpdate={(data) => setImageCal(data)}
          />
          <CalibrationPanel
            type="video"
            icon={<Video className="w-5 h-5" />}
            label="Video Generation"
            accentColor="amber"
            data={videoCal}
            onUpdate={(data) => setVideoCal(data)}
          />
        </div>
      </div>

      {/* Card 2: Per-User Cost Projection */}
      {hasAnyCalibration && (
        <div className="card-glow rounded-2xl p-6 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Per-User Cost Projection</h2>
              <p className="text-xs text-muted-foreground">
                Estimate monthly cost based on expected usage
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <SliderInput
              label="Images per user / month"
              value={imagesPerMonth}
              onChange={setImagesPerMonth}
              min={0}
              max={500}
              step={5}
              disabled={imageCal?.status !== 'completed'}
              disabledLabel="Not calibrated"
            />
            <SliderInput
              label="Videos per user / month"
              value={videosPerMonth}
              onChange={setVideosPerMonth}
              min={0}
              max={100}
              step={1}
              disabled={videoCal?.status !== 'completed'}
              disabledLabel="Not calibrated"
            />
          </div>

          {/* Cost breakdown */}
          <div className="bg-secondary/30 rounded-xl p-5 font-mono text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Image cost</span>
              <span>
                {imagesPerMonth} x ${imageCostPerUnit.toFixed(4)} ={' '}
                <span className="text-foreground font-semibold">${projection.imageCostPerUser.toFixed(2)}</span>
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Video cost</span>
              <span>
                {videosPerMonth} x ${videoCostPerUnit.toFixed(4)} ={' '}
                <span className="text-foreground font-semibold">${projection.videoCostPerUser.toFixed(2)}</span>
              </span>
            </div>
            <div className="border-t border-border/50 pt-2 flex justify-between">
              <span className="text-foreground font-semibold">Total cost per user / month</span>
              <span className="text-cyan-400 font-bold text-base">${projection.totalCostPerUser.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Card 3: Subscription Scenario Modeler */}
      {hasAnyCalibration && (
        <div className="card-glow rounded-2xl p-6 animate-fade-in-up" style={{ animationDelay: '300ms' }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Subscription Scenario Modeler</h2>
              <p className="text-xs text-muted-foreground">
                Set a price and see your margins
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <NumberInput
              label="Subscription Price ($/month)"
              value={subscriptionPrice}
              onChange={setSubscriptionPrice}
              min={0}
              step={0.01}
              prefix="$"
            />
            <NumberInput
              label="Number of Subscribers"
              value={subscribers}
              onChange={setSubscribers}
              min={1}
              step={1}
            />
          </div>

          {/* Per-user breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-secondary/30 rounded-xl p-5 font-mono text-sm space-y-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 font-sans">
                Per User Breakdown
              </h3>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Images</span>
                <span>{imagesPerMonth} x ${imageCostPerUnit.toFixed(4)} = ${projection.imageCostPerUser.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Videos</span>
                <span>{videosPerMonth} x ${videoCostPerUnit.toFixed(4)} = ${projection.videoCostPerUser.toFixed(2)}</span>
              </div>
              <div className="border-t border-border/50 pt-2 space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cost / user</span>
                  <span className="text-foreground">${projection.totalCostPerUser.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Price / user</span>
                  <span className="text-foreground">${subscriptionPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-foreground">Margin / user</span>
                  <span className={cn(
                    'font-bold',
                    projection.marginPercent > 50 ? 'text-emerald-400' :
                    projection.marginPercent > 20 ? 'text-amber-400' : 'text-rose-400'
                  )}>
                    ${projection.marginPerUser.toFixed(2)} ({projection.marginPercent.toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-secondary/30 rounded-xl p-5 font-mono text-sm space-y-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 font-sans">
                Aggregate ({subscribers.toLocaleString()} subscribers)
              </h3>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Monthly revenue</span>
                <span className="text-foreground">${projection.monthlyRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Monthly cost</span>
                <span className="text-foreground">${projection.monthlyCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="border-t border-border/50 pt-2 flex justify-between">
                <span className="font-semibold text-foreground">Monthly profit</span>
                <span className={cn(
                  'font-bold text-base',
                  projection.monthlyProfit > 0 ? 'text-emerald-400' : 'text-rose-400'
                )}>
                  ${projection.monthlyProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          {/* Margin health indicator */}
          <div className="mt-4 flex items-center gap-3">
            <div className="h-2 flex-1 rounded-full overflow-hidden bg-secondary/50">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  projection.marginPercent > 50 ? 'bg-emerald-500' :
                  projection.marginPercent > 20 ? 'bg-amber-500' : 'bg-rose-500'
                )}
                style={{ width: `${Math.max(0, Math.min(100, projection.marginPercent))}%` }}
              />
            </div>
            <span className={cn(
              'text-xs font-medium min-w-[80px] text-right',
              projection.marginPercent > 50 ? 'text-emerald-400' :
              projection.marginPercent > 20 ? 'text-amber-400' : 'text-rose-400'
            )}>
              {projection.marginPercent > 50 ? 'Healthy' :
               projection.marginPercent > 20 ? 'Moderate' : 'Low Margin'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Sub-components ---

function CalibrationPanel({
  type,
  icon,
  label,
  accentColor,
  data,
  onUpdate,
}: {
  type: 'image' | 'video';
  icon: React.ReactNode;
  label: string;
  accentColor: 'violet' | 'amber';
  data: CalibrationData | null;
  onUpdate: (data: CalibrationData | null) => void;
}) {
  const [balance, setBalance] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);

  const isInProgress = data?.status === 'in_progress';
  const isCompleted = data?.status === 'completed';

  const handleStart = async () => {
    const val = parseFloat(balance);
    if (isNaN(val) || val < 0) {
      setPanelError('Enter a valid balance');
      return;
    }

    setSubmitting(true);
    setPanelError(null);
    try {
      const res = await calibration.start(type, val);
      onUpdate(res.calibration);
      setBalance('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start';
      console.error('Calibration start error:', err);
      setPanelError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnd = async () => {
    const val = parseFloat(balance);
    if (isNaN(val) || val < 0) {
      setPanelError('Enter a valid balance');
      return;
    }

    setSubmitting(true);
    setPanelError(null);
    try {
      const res = await calibration.end(type, val);
      onUpdate(res.calibration);
      setBalance('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to end';
      console.error('Calibration end error:', err);
      setPanelError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecalibrate = async () => {
    setSubmitting(true);
    setPanelError(null);
    try {
      await calibration.remove(type);
      onUpdate(null);
      setBalance('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset';
      console.error('Calibration reset error:', err);
      setPanelError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const bgAccent = accentColor === 'violet' ? 'bg-violet-500/10 border-violet-500/20' : 'bg-amber-500/10 border-amber-500/20';
  const textAccent = accentColor === 'violet' ? 'text-violet-400' : 'text-amber-400';
  const iconBg = accentColor === 'violet' ? 'bg-violet-500/20' : 'bg-amber-500/20';

  return (
    <div className={cn('rounded-xl border p-5', bgAccent)}>
      <div className="flex items-center gap-2 mb-4">
        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', iconBg)}>
          <span className={textAccent}>{icon}</span>
        </div>
        <h3 className="font-semibold">{label}</h3>
        {isCompleted && (
          <span className="ml-auto text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
            Calibrated
          </span>
        )}
        {isInProgress && (
          <span className="ml-auto text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            In Progress
          </span>
        )}
      </div>

      {panelError && (
        <div className="mb-3 text-xs text-rose-400 bg-rose-500/10 rounded-lg px-3 py-2">
          {panelError}
        </div>
      )}

      {/* Not calibrated state */}
      {!data && (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">RunPod Balance ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
              placeholder="e.g. 25.00"
              className="w-full bg-secondary/50 border border-border/50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-500/50"
            />
          </div>
          <button
            onClick={handleStart}
            disabled={submitting || !balance}
            className="flex items-center gap-2 w-full justify-center px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500/20 to-violet-500/20 text-cyan-400 text-sm font-medium hover:from-cyan-500/30 hover:to-violet-500/30 disabled:opacity-50 transition-all"
          >
            <Play className="w-4 h-4" />
            Start Calibration
          </button>
        </div>
      )}

      {/* In progress state */}
      {isInProgress && (
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground space-y-1">
            <div>Start balance: <span className="text-foreground">${data.startBalance.toFixed(2)}</span></div>
            <div>Started: <span className="text-foreground">{new Date(data.startTime).toLocaleString()}</span></div>
          </div>
          <p className="text-xs text-muted-foreground italic">
            Go generate {type === 'image' ? 'images' : 'videos'} in the app, then enter your new balance below.
          </p>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">End Balance ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
              placeholder="e.g. 24.10"
              className="w-full bg-secondary/50 border border-border/50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-500/50"
            />
          </div>
          <button
            onClick={handleEnd}
            disabled={submitting || !balance}
            className="flex items-center gap-2 w-full justify-center px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 text-emerald-400 text-sm font-medium hover:from-emerald-500/30 hover:to-cyan-500/30 disabled:opacity-50 transition-all"
          >
            <Square className="w-4 h-4" />
            End Calibration
          </button>
        </div>
      )}

      {/* Completed state */}
      {isCompleted && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <StatMini
              label="Cost per unit"
              value={`$${data.costPerUnit!.toFixed(4)}`}
              accent="cyan"
            />
            <StatMini
              label="Generations"
              value={data.totalGenerations!.toString()}
              accent="violet"
            />
            <StatMini
              label="Total spend"
              value={`$${(data.startBalance - data.endBalance!).toFixed(2)}`}
              accent="emerald"
            />
            <StatMini
              label="Calibrated"
              value={new Date(data.endTime!).toLocaleDateString()}
              accent="muted"
            />
          </div>
          <button
            onClick={handleRecalibrate}
            disabled={submitting}
            className="flex items-center gap-2 w-full justify-center px-4 py-2 rounded-lg bg-secondary/50 text-muted-foreground text-sm font-medium hover:bg-secondary hover:text-foreground disabled:opacity-50 transition-all"
          >
            <RotateCcw className="w-4 h-4" />
            Recalibrate
          </button>
        </div>
      )}
    </div>
  );
}

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: 'cyan' | 'violet' | 'emerald' | 'muted';
}) {
  const valueColor = {
    cyan: 'text-cyan-400',
    violet: 'text-violet-400',
    emerald: 'text-emerald-400',
    muted: 'text-muted-foreground',
  }[accent];

  return (
    <div className="bg-secondary/30 rounded-lg p-2.5">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={cn('text-sm font-semibold mt-0.5', valueColor)}>{value}</p>
    </div>
  );
}

function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  disabled,
  disabledLabel,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  disabledLabel?: string;
}) {
  return (
    <div className={cn(disabled && 'opacity-50')}>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm text-muted-foreground">{label}</label>
        {disabled ? (
          <span className="text-xs text-muted-foreground italic">{disabledLabel}</span>
        ) : (
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(Math.max(min, Math.min(max, parseFloat(e.target.value) || 0)))}
            className="w-20 bg-secondary/50 border border-border/50 rounded-lg px-2 py-1 text-sm text-right focus:outline-none focus:border-cyan-500/50"
          />
        )}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        disabled={disabled}
        className="w-full accent-cyan-500"
      />
    </div>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  min,
  step,
  prefix,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  step: number;
  prefix?: string;
}) {
  return (
    <div>
      <label className="text-sm text-muted-foreground block mb-2">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            {prefix}
          </span>
        )}
        <input
          type="number"
          min={min}
          step={step}
          value={value}
          onChange={(e) => onChange(Math.max(min, parseFloat(e.target.value) || 0))}
          className={cn(
            'w-full bg-secondary/50 border border-border/50 rounded-lg py-2 text-sm focus:outline-none focus:border-cyan-500/50',
            prefix ? 'pl-7 pr-3' : 'px-3'
          )}
        />
      </div>
    </div>
  );
}
